/**
 * @file: VisibilityPicker.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Visit-visibility chooser. An action sheet can't explain its options,
 * so this uses the shared BottomPanel to give each level a one-line description
 * of who can see the lead. Picking a level applies it and closes.
 */

import { Typography } from '@euler/app/components/typography';
import { LD } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { BottomPanel } from '@euler/components/BottomPanel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { LeadVisibility, LeadVisibilityLabel } from '@euler/model/lead/Lead';
import {
  Check,
  CircleDotDashed,
  Eye,
  EyeClosed,
  LucideIcon,
} from 'lucide-react-native';
import { memo } from 'react';
import { TouchableOpacity, View } from 'react-native';

const OPTIONS: { value: LeadVisibility; icon: LucideIcon; desc: string }[] = [
  { value: 'public', icon: Eye, desc: '所有服务顾问和门店主管可见' },
  {
    value: 'restricted',
    icon: CircleDotDashed,
    desc: '仅创建人、负责人和门店主管可见',
  },
  { value: 'private', icon: EyeClosed, desc: '仅创建人和负责人可见' },
];

export const VisibilityPicker = memo(
  ({
    visible,
    value,
    onClose,
    onSelect,
  }: {
    visible: boolean;
    value: LeadVisibility;
    onClose: () => void;
    onSelect: (next: LeadVisibility) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <BottomPanel visible={visible} onClose={onClose} fitContent>
        <View style={{ padding: rpx(20) }}>
          <Typography.Text size={rpx(17)} color={LD.ink} bold>
            设置可见范围
          </Typography.Text>
          {OPTIONS.map(opt => {
            const selected = opt.value === value;
            const Icon = opt.icon;
            return (
              <TouchableOpacity
                key={opt.value}
                activeOpacity={0.7}
                onPress={() => onSelect(opt.value)}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  paddingVertical: rpx(14),
                  paddingHorizontal: rpx(14),
                  borderRadius: rpx(14),
                  marginTop: rpx(12),
                  backgroundColor: selected ? LD.brandSoft : LD.canvas,
                }}
              >
                <View
                  style={{
                    width: rpx(38),
                    height: rpx(38),
                    borderRadius: rpx(19),
                    backgroundColor: selected ? LD.brand : '#fff',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon size={rpx(18)} color={selected ? '#fff' : LD.steel} />
                </View>
                <View style={{ flex: 1, marginLeft: rpx(12) }}>
                  <Typography.Text size={rpx(15)} color={LD.ink} medium>
                    {LeadVisibilityLabel[opt.value]}
                  </Typography.Text>
                  <Typography.Text size={rpx(12)} color={LD.steel} mt={rpx(2)}>
                    {opt.desc}
                  </Typography.Text>
                </View>
                {selected ? <Check size={rpx(18)} color={LD.brand} /> : null}
              </TouchableOpacity>
            );
          })}
        </View>
      </BottomPanel>
    );
  },
);
